// @flow
import React from 'react';
import i18n from 'app/common/lib/i18n';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import resourceFactory from 'app/common/lib/ResourceFactory';
import withPromiseCancellation from 'app/common/lib/react/withPromiseCancellation';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Text, Flyout, Touchable } from 'gestalt';

const MAX_RECOMMENDATIONS = 5;

const ESC_KEYCODE = 27;
const UP_KEYCODE = 38;
const DOWN_KEYCODE = 40;
const ENTER_KEYCODE = 13;

type HashtagFlyoutPropType = {
  addHashtag: Function,
  anchor: HTMLElement,
  editing: boolean,
  makeCancelablePromise: (promise: Promise<any>, uniqueKey?: string) => Promise<any>,
  query: string,
  idealDirection?: 'up' | 'right' | 'down' | 'left',
};

type HashtagFlyoutStateType = {
  selectedIndex: number,
  isOpen: boolean,
  recommendations: Array<Object>,
  resourceLoaded: boolean,
};

// NOTE: When flyout opens, it automatically steals focus from LegacyTextArea.
// To prevent LegacyTextArea from closing, it steals focus from the flyout
// in PinPane.js line 130. Please see LegacyTextArea.js, PinPane.js (line 130)
// and PinEditDescription (line 42 if problems occur).

class HashtagFlyout extends React.Component<HashtagFlyoutPropType, HashtagFlyoutStateType> {
  state: HashtagFlyoutStateType = {
    isOpen: this.props.editing,
    recommendations: [],
    selectedIndex: 0,
    resourceLoaded: false,
  };

  componentDidMount() {
    this.mounted = true;

    this.callResource(this.props.query);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps: HashtagFlyoutPropType) {
    if (nextProps.query !== this.props.query && nextProps.query !== '') {
      this.callResource(nextProps.query);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);

    this.mounted = false;
  }

  onDismiss = () => {
    this.setState({ isOpen: false });
  };

  mounted = false;

  addHashtag = (hashtag: string) => {
    this.props.addHashtag(hashtag);
    this.setState({ recommendations: [] });
  };

  handleKeyDown = (evt: SyntheticKeyboardEvent<>) => {
    if (evt.keyCode === UP_KEYCODE) {
      this.handlePrevHashtag(evt);
    } else if (evt.keyCode === DOWN_KEYCODE) {
      this.handleNextHashtag(evt);
    } else if (evt.keyCode === ESC_KEYCODE) {
      this.handleClose(evt);
    } else if (evt.keyCode === ENTER_KEYCODE) {
      this.handleSubmit(evt);
    }
  };

  handlePrevHashtag = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    if (this.state.selectedIndex > 0) {
      this.setState((prevState) => ({
        selectedIndex: prevState.selectedIndex - 1,
      }));
    }
  };

  handleNextHashtag = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    if (this.state.selectedIndex < this.state.recommendations.length - 1) {
      this.setState((prevState) => ({
        selectedIndex: prevState.selectedIndex + 1,
      }));
    }
  };

  handleClose = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    this.setState({ recommendations: [] });
  };

  handleSubmit = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    this.addHashtag(this.state.recommendations[this.state.selectedIndex].query);
  };

  /**
   * If user changes query before resource has returned, hashtag flyout either
   * unmounts or rerenders, before setState can be called. Therefore by having
   * the callback as cancellable Promise, this enables hashtag flyout to cancel
   * the promise and prevent setState from being called when unmounting or a new
   * search query is passed in.
   */
  callResource = (query: string) => {
    const { makeCancelablePromise } = this.props;
    makeCancelablePromise(
      resourceFactory.create('HashtagTypeaheadResource', { query, showPinCount: true }).callGet(),
      'typeahead',
    )
      .then((response) => {
        if (!this.mounted) {
          return;
        }

        const data = response.resource_response.data;
        this.setState({
          recommendations: data.slice(0, MAX_RECOMMENDATIONS),
          resourceLoaded: true,
        });
      })
      .catch(({ isCanceled }) => {
        if (!isCanceled) {
          this.setState({ resourceLoaded: false });
        }
      });
  };

  render() {
    const { recommendations, resourceLoaded, selectedIndex } = this.state;
    const { anchor, idealDirection } = this.props;
    const queryRecommendations = recommendations.filter((rec) => rec.query);

    const idealDirectionActual = idealDirection || 'down';
    const flyoutKey = 'flyout_' + queryRecommendations.length;

    return resourceLoaded && queryRecommendations.length > 0 ? (
      <div className="HashtagFlyout">
        <Flyout
          key={flyoutKey}
          anchor={anchor}
          idealDirection={idealDirectionActual}
          onDismiss={this.onDismiss}
          size="sm"
        >
          <div className="HashtagRecommendations">
            {queryRecommendations.length > 0
              ? queryRecommendations.map((tag, index) => (
                  <div className="HashtagItem" key={tag.query}>
                    <Touchable onTouch={(q) => this.addHashtag(tag.query)} fullWidth={false}>
                      <Box
                        display="flex"
                        justifyContent="between"
                        alignItems="center"
                        color={index === selectedIndex ? 'lightGray' : 'transparent'}
                        deprecatedPadding={1}
                      >
                        <Box flex="grow">
                          <Text inline size="sm" weight="bold">
                            {tag.query}
                          </Text>
                        </Box>
                        <Box flex="none">
                          <Text inline size="xs">
                            {interpolateNamedTemplate(
                              i18n.ngettext(
                                '{{ count }} pin',
                                '{{ count }} pins',
                                Number(tag.pin_count),
                                'label for n pins in hashtag flyout',
                              ),
                              {
                                count: (
                                  <PrettyNumber value={Number(tag.pin_count)} key="pinCount" />
                                ),
                              },
                            )}
                          </Text>
                        </Box>
                      </Box>
                    </Touchable>
                  </div>
                ))
              : null}
          </div>
        </Flyout>
      </div>
    ) : null;
  }
}

export default withPromiseCancellation(HashtagFlyout);
